import { z } from 'zod';
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import type { CustomFieldDefinition } from '../types/custom-field-definitions.types';
import type {
  CreateContactRequestApi,
  UpdateContactRequestApi,
  UpdateSubscriptionStatusRequestApi,
} from '../types/contacts-api.types';
import type { Contact } from '../types/contacts.types';
import type { ChannelStatusLabel } from './contact-subscription-status';
import { getEmailChannelStatus, getSmsChannelStatus } from './contact-subscription-status';
import { getContactLists } from './contact-attributes';
import {
  getEditableCustomFieldDefinitions,
  mapContactCustomFieldsToFormValues,
} from './custom-field-attributes.utils';

export const CONTACT_LOCALE_OPTIONS = [
  { value: 'en-CH', label: 'English (Switzerland)' },
  { value: 'de-CH', label: 'German (Switzerland)' },
  { value: 'fr-CH', label: 'French (Switzerland)' },
  { value: 'it-CH', label: 'Italian (Switzerland)' },
  { value: 'en-US', label: 'English (US)' },
] as const;

export const SUBSCRIPTION_OPTIONS = ['Pending', 'Subscribed', 'Unsubscribed'] as const;

export type ApiSubscriptionStatus = 'subscribed' | 'unsubscribed' | 'pending';

export const toApiSubscriptionStatus = (value: string): ApiSubscriptionStatus => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'subscribed' || normalized === 'unsubscribed' || normalized === 'pending') {
    return normalized;
  }
  return 'pending';
};

const optionalText = z.string().trim().optional().or(z.literal(''));

const optionalPhone = z.string().superRefine((value, ctx) => {
  const trimmed = value.trim();
  if (!trimmed) return;

  if (!isPossiblePhoneNumber(trimmed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Phone number length is invalid for the selected country',
    });
    return;
  }

  if (!isValidPhoneNumber(trimmed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter a valid phone number for the selected country',
    });
  }
});

export const CONTACT_NAME_MAX_LENGTH = 100;

const baseContactDrawerShape = {
  email: z
    .string()
    .trim()
    .refine((value) => !value || z.string().email().safeParse(value).success, {
      message: 'Enter a valid email address',
    }),
  phone: optionalPhone,
  firstName: z
    .string()
    .trim()
    .min(1, { message: 'First name is required' })
    .max(CONTACT_NAME_MAX_LENGTH, {
      message: `First name must be ${CONTACT_NAME_MAX_LENGTH} characters or fewer`,
    }),
  lastName: z
    .string()
    .trim()
    .min(1, { message: 'Last name is required' })
    .max(CONTACT_NAME_MAX_LENGTH, {
      message: `Last name must be ${CONTACT_NAME_MAX_LENGTH} characters or fewer`,
    }),
  locale: optionalText,
  subEmail: z.enum(SUBSCRIPTION_OPTIONS),
  subSms: z.enum(SUBSCRIPTION_OPTIONS),
  listIds: z.array(z.string()),
  customFields: z.record(z.string(), z.string()),
};

/** Builds the schema; required custom fields are checked inline against the org's definitions. */
export const createContactDrawerFormSchema = (definitions: CustomFieldDefinition[] = []) =>
  z.object(baseContactDrawerShape).superRefine((values, ctx) => {
    if (!values.email.trim() && !values.phone.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Enter an email or a phone number',
      });
    }

    getEditableCustomFieldDefinitions(definitions).forEach((definition) => {
      if (!definition.Required) return;
      if (values.customFields?.[definition.Key]?.trim()) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customFields', definition.Key],
        message: `${definition.Label} is required`,
      });
    });
  });

export const contactDrawerFormSchema = createContactDrawerFormSchema();

export type ContactDrawerFormValues = z.infer<typeof contactDrawerFormSchema>;

export const CONTACT_DRAWER_FORM_DEFAULTS: ContactDrawerFormValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  locale: 'en-CH',
  subEmail: 'Pending',
  subSms: 'Unsubscribed',
  listIds: [],
  customFields: {},
};

const toOptionalString = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toSubscriptionValue = (
  status: ChannelStatusLabel | null | undefined
): (typeof SUBSCRIPTION_OPTIONS)[number] => {
  if (status === 'Subscribed' || status === 'Pending' || status === 'Unsubscribed') {
    return status;
  }
  return 'Unsubscribed';
};

const toApiDateValue = (value: string): string => {
  if (value.includes('T')) {
    return value;
  }
  return `${value}T00:00:00.000Z`;
};

const buildRestAttributeValue = (
  definition: CustomFieldDefinition & { Key: string; DataType: string },
  formValue: string | undefined
): unknown | undefined => {
  const dataType = definition.DataType.trim();
  const trimmed = formValue?.trim() ?? '';

  if (dataType === 'Boolean') {
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    return undefined;
  }

  if (!trimmed) {
    return undefined;
  }

  if (dataType === 'Number' || dataType === 'NumberRange') {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (dataType === 'Date') {
    return toApiDateValue(trimmed);
  }

  if (dataType === 'MultiSelect') {
    const items = trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length > 0 ? items : undefined;
  }

  return trimmed;
};

export const buildRestAttributesObject = (
  definitions: CustomFieldDefinition[],
  formValues: Record<string, string> | undefined
): Record<string, unknown> | undefined => {
  const attributes: Record<string, unknown> = {};

  getEditableCustomFieldDefinitions(definitions).forEach((definition) => {
    const value = buildRestAttributeValue(definition, formValues?.[definition.Key]);
    if (value !== undefined) {
      attributes[definition.Key] = value;
    }
  });

  return Object.keys(attributes).length > 0 ? attributes : undefined;
};

export const buildCreateContactRestRequest = (
  values: ContactDrawerFormValues,
  definitions: CustomFieldDefinition[] = []
): CreateContactRequestApi => {
  const email = toOptionalString(values.email);
  const phone = toOptionalString(values.phone);
  const attributes = buildRestAttributesObject(definitions, values.customFields);
  const listIds = values.listIds.filter(Boolean);

  return {
    email: email ?? null,
    phone: phone ?? null,
    firstName: toOptionalString(values.firstName) ?? null,
    lastName: toOptionalString(values.lastName) ?? null,
    locale: toOptionalString(values.locale) ?? null,
    listIds: listIds.length > 0 ? listIds : null,
    attributes: attributes ?? null,
    subscriptions: {
      Email: toApiSubscriptionStatus(values.subEmail),
      Sms: toApiSubscriptionStatus(values.subSms),
    },
  };
};

export const buildUpdateContactRestRequest = (
  values: ContactDrawerFormValues,
  definitions: CustomFieldDefinition[] = []
): UpdateContactRequestApi => {
  const email = toOptionalString(values.email);
  const phone = toOptionalString(values.phone);
  const attributes = buildRestAttributesObject(definitions, values.customFields);

  return {
    email: email ?? null,
    phone: phone ?? null,
    firstName: toOptionalString(values.firstName) ?? null,
    lastName: toOptionalString(values.lastName) ?? null,
    locale: toOptionalString(values.locale) ?? null,
    attributes: attributes ?? null,
  };
};

export const buildUpdateSubscriptionRestRequest = (
  values: ContactDrawerFormValues,
  contact: Contact
): UpdateSubscriptionStatusRequestApi | null => {
  const currentEmail = toSubscriptionValue(getEmailChannelStatus(contact));
  const currentSms = toSubscriptionValue(getSmsChannelStatus(contact));

  const request: UpdateSubscriptionStatusRequestApi = {};
  let hasChanges = false;

  if (values.subEmail !== currentEmail) {
    request.emailSubscriptionStatus = toApiSubscriptionStatus(values.subEmail);
    hasChanges = true;
  }

  if (values.subSms !== currentSms) {
    request.smsSubscriptionStatus = toApiSubscriptionStatus(values.subSms);
    hasChanges = true;
  }

  // No `source`: the API derives it from the caller (`manual` for a person in the portal), and
  // the PascalCase value this used to send was outside its lowercase vocabulary.
  return hasChanges ? request : null;
};

export type SubscriptionChannel = 'Email' | 'Sms';

export const buildChannelSubscriptionPatchRequest = (
  channel: SubscriptionChannel,
  currentStatus: ChannelStatusLabel
): UpdateSubscriptionStatusRequestApi | null => {
  if (currentStatus === 'Suppressed' || currentStatus === 'Pending') {
    return null;
  }

  const nextStatus: ApiSubscriptionStatus =
    currentStatus === 'Subscribed' ? 'unsubscribed' : 'subscribed';

  return channel === 'Email'
    ? { emailSubscriptionStatus: nextStatus }
    : { smsSubscriptionStatus: nextStatus };
};

type ListRef = {
  id: string;
  name: string;
};

const resolveListIdsFromContact = (contact: Contact, lists: ListRef[]): string[] => {
  const listNames = new Set(getContactLists(contact).map((name) => name.trim().toLowerCase()));
  if (listNames.size === 0) return [];

  return lists
    .filter((list) => {
      const name = list.name.trim().toLowerCase();
      return Boolean(name && listNames.has(name));
    })
    .map((list) => list.id)
    .filter(Boolean);
};

export const mapContactToDrawerFormValues = (
  contact: Contact,
  definitions: CustomFieldDefinition[] = [],
  lists: ListRef[] = []
): ContactDrawerFormValues => ({
  email: contact.Email?.trim() ?? '',
  phone: contact.Phone?.trim() ?? '',
  firstName: contact.FirstName?.trim() ?? '',
  lastName: contact.LastName?.trim() ?? '',
  locale: contact.Locale?.trim() || contact.Language?.trim() || 'en-CH',
  subEmail: toSubscriptionValue(getEmailChannelStatus(contact)),
  subSms: toSubscriptionValue(getSmsChannelStatus(contact)),
  listIds: resolveListIdsFromContact(contact, lists),
  customFields: mapContactCustomFieldsToFormValues(contact, definitions),
});
